/**
 * @file   src\components\FreeVacuumTemplate03.tsx
 * @brief  Free vacuum small sign template.
 * @date   SEPTEMBER, 2026
 * @author ZCO Engineer
 * @copyright (c) 2024, ZCO
 */
import {
  React,
  useState,
  Button,
  useEffect,
} from "../utils/thirdpartyComponents";
import "../styles/signtemplate.scss";
import QRCode from "qrcode.react";
import html2canvas from "html2canvas";
import { toPng, toJpeg } from "html-to-image";
import jsPDF from "jspdf";
import FullLoader from "../components/FullLoader";
import CWCheckbox from "./CWCheckbox";
import { EquipmentType } from "../utils/enums";
   
const FreeVacuumTemplate03 = (props: any) => {

  const [loading, setLoading] = useState(false);
  const [companyLogoBase64, setCompanyLogoBase64] = useState<string | null>(
    null
  );
  const [agreeTerms, setAgreeTerms] = useState<boolean>(false);

  useEffect(() => {
    setCompanyLogoBase64(props.companyLogoUrl);
  }, [props.companyLogoUrl]);
  const captureId = "capture";

  // Download as PDF
  const downloadAsPDF = async () => {
    setLoading(true);
    const element = document.getElementById(captureId);
    if (!element) return;

    // Convert inches to millimeters
    const pdfWidthMM = 203.2;   // 8 inches
    const pdfHeightMM = 304.8; // 12 inches

    // Set pixel dimensions for high DPI (300 DPI)
    const targetWidth = 2400;
    const targetHeight = 3600;

    if (navigator.userAgent.toLowerCase().includes("firefox")) {
      try {
        const canvas = await html2canvas(element, {
          scale: 1, // we'll resize manually
          useCORS: true,
          logging: false,
          width: targetWidth,
          height: targetHeight,
        });

        const resizedCanvas = document.createElement("canvas");
        resizedCanvas.width = targetWidth;
        resizedCanvas.height = targetHeight;

        const ctx = resizedCanvas.getContext("2d");
        ctx?.drawImage(canvas, 0, 0, targetWidth, targetHeight);

        const imgData = resizedCanvas.toDataURL("image/png");
        const pdf = new jsPDF({
          orientation: "portrait",
          unit: "mm",
          format: [pdfWidthMM, pdfHeightMM],
        });

        pdf.addImage(imgData, "PNG", 0, 0, pdfWidthMM, pdfHeightMM);
        pdf.save(`${props.washname}-template-3.pdf`);
      } catch (error) {
        console.error("Error generating high-quality PDF:", error);
      } finally {
        setLoading(false);
      }
    } else {
      try {
        const dataUrl = await toPng(element, {
          quality: 1.0,
          canvasWidth: targetWidth,
          canvasHeight: targetHeight,
          pixelRatio: 1, // manually setting width/height, so keep ratio 1
          cacheBust: true,
        });

        const pdf = new jsPDF({
          orientation: "portrait",
          unit: "mm",
          format: [pdfWidthMM, pdfHeightMM],
        });

        pdf.addImage(dataUrl, "PNG", 0, 0, pdfWidthMM, pdfHeightMM);
        pdf.save(`${props.washname}-template-3.pdf`);
      } catch (error) {
        console.error("Error generating high-quality PDF:", error);
      } finally {
        setLoading(false);
      }
    }
  };


  // Download as JPG
  const downloadAsJPG = async () => {
    setLoading(true);
    const element = document.getElementById(captureId);
    if (!element) return;

    const targetWidth = 2400; // 8 inches at 300 DPI
    const targetHeight = 3600; // 12 inches at 300 DPI

    if (navigator.userAgent.toLowerCase().includes("firefox")) {
      try {
        const canvas = await html2canvas(element, {
          scale: 1, // Keep scale 1 to let us control canvas size manually
          useCORS: true,
          logging: false,
          width: targetWidth,
          height: targetHeight,
        });

        const resizedCanvas = document.createElement("canvas");
        resizedCanvas.width = targetWidth;
        resizedCanvas.height = targetHeight;

        const ctx = resizedCanvas.getContext("2d");
        ctx?.drawImage(canvas, 0, 0, targetWidth, targetHeight);

        const link = document.createElement("a");
        link.download = `${props.washname}-template-3.jpg`;
        link.href = resizedCanvas.toDataURL("image/jpeg", 1.0);
        link.click();
      } catch (error) {
        console.error("Error generating high-quality JPG:", error);
      } finally {
        setLoading(false);
      }
    } else {
      try {
        const dataUrl = await toJpeg(element, {
          quality: 1.0,
          canvasWidth: targetWidth,
          canvasHeight: targetHeight,
          pixelRatio: 1, // Already specifying the dimensions directly
          cacheBust: true,
        });

        const link = document.createElement("a");
        link.href = dataUrl;
        link.download = `${props.washname}-template-3.jpg`;
        link.click();
      } catch (error) {
        console.error("Error generating high-quality JPG:", error);
      } finally {
        setLoading(false);
      }
    }
  };


  // Download as PNG
  const downloadAsPNG = async () => {
    setLoading(true);
    const element = document.getElementById(captureId);
    if (!element) return;

    // Target export dimensions: 8" x 12" @ 300 DPI → 2400px x 3600px
    const targetWidth = 2400;
    const targetHeight = 3600;

    if (navigator.userAgent.toLowerCase().includes("firefox")) {
      try {
        const canvas = await html2canvas(element, {
          scale: 1,
          useCORS: true,
          logging: false,
          width: targetWidth,
          height: targetHeight,
        });

        // Resize to exact dimensions
        const resizedCanvas = document.createElement("canvas");
        resizedCanvas.width = targetWidth;
        resizedCanvas.height = targetHeight;
        const ctx = resizedCanvas.getContext("2d");
        ctx?.drawImage(canvas, 0, 0, targetWidth, targetHeight);

        const link = document.createElement("a");
        link.download = `${props.washname}-template-3.png`;
        link.href = resizedCanvas.toDataURL("image/png");
        link.click();
      } catch (error) {
        console.error("Error generating high-quality PNG:", error);
      } finally {
        setLoading(false);
      }
    } else {
      try {
        const dataUrl = await toPng(element, {
          cacheBust: true,
          pixelRatio: 1,
          canvasWidth: targetWidth,
          canvasHeight: targetHeight,
        });

        const link = document.createElement("a");
        link.href = dataUrl;
        link.download = `${props.washname}-template-3.png`;
        link.click();
      } catch (error) {
        console.error("Error generating high-quality PNG:", error);
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <div>
      <div id={captureId} className="template-free-vacuum">
        <div className="template-header text-center position-relative">
          <div className="logo d-flex align-items-center justify-content-center mx-auto">
            {companyLogoBase64 ? (
              <img src={companyLogoBase64} alt="Car Wash App" />
            ) : (
              <span>Loading logo...</span>
            )}
          </div>
          <h1
            className="f-extrabold"
            style={{ fontSize: `${props.washnamefontsize}` }}
          >
            {props.washname}
          </h1>
        </div>
        <div className="template-content text-center position-relative">
          <div className="promo-banner mx-auto">
            <h2 className="f-extrabold">{props.titlecontent}</h2>
          </div>
          <div className="app-download mx-auto">
            <div className="app-qr mx-auto">
              <QRCode
                value={props.appUrl}
                size={90}
                level={"H"}
                includeMargin={true}
                renderAs="svg"
                crossOrigin="anonymous"
              />
            </div>
            <h3 className="f-extrabold">SCAN TO GET THE APP</h3>
            <p className="f-medium">APP URL: {props.appUrl}</p>
          </div>
          <div className="steps-card mx-auto">
            <div className="step-item">
              <span className="step-pill f-extrabold">STEP 1</span>
              <h3
                className="f-extrabold"
                style={{ fontSize: `${props.box1fontsize}` }}
              >
                {props.subscriptionbox1}
              </h3>
            </div>
            <div className="step-item">
              <span className="step-pill f-extrabold">STEP 2</span>
              <h3
                className="f-extrabold"
                style={{ fontSize: `${props.box2fontsize}` }}
              >
                {props.subscriptionbox2}
              </h3>
            </div>
            <div className="step-item">
              <span className="step-pill f-extrabold">STEP 3</span>
              <h3
                className="f-extrabold"
                style={{ fontSize: `${props.box3fontsize}` }}
              >
                {props.subscriptionbox3}
              </h3>
            </div>
          </div>
          <div className="bay-scan-block mx-auto d-flex align-items-center justify-content-between">
            <div className="bay-scan-copy text-start">
              <p className="f-bold">SCAN CODE IN THE APP TO START</p>
              <h2 className="f-extrabold">{props.bayName}</h2>
            </div>
            <div className="bay-qr-cover d-flex flex-column">
              <div className="bay-qr-wrap">
              <div className="bay-qr mx-auto">
                <QRCode
                  value={props.bayId}
                  size={150}
                  level={"H"}
                  includeMargin={true}
                  renderAs="svg"
                  crossOrigin="anonymous"
                />
              </div>
              
            </div>
            <div className="bay-name-pill f-extrabold">{props.bayName}</div>
            </div>
          </div>
          {props.bayType != EquipmentType['In Bay'] && props.bayType != EquipmentType['Tunnel'] && (
            <div className="stop-block mx-auto d-flex align-items-center">
              <div className="stop-badge f-extrabold">STOP!</div>
              <p className="f-extrabold text-start">
                <span>APP USERS:</span> USE STOP BUTTON ON APP TO STOP TIMER WHEN DONE
              </p>
            </div>
          )}
        </div>
      </div>
      <div className="mt-3">
        <CWCheckbox
          id="agree"
          name="agree"
          label="By checking this box, I (carwash owner) acknowledge that I have proofread the sign and checked all QR codes.  Any reprints will be the responsibility of the carwash owner."
          checked={agreeTerms}
          onChange={(event: any) => setAgreeTerms(event.target.checked)}
        />
      </div>
      <div className="btn-sec d-flex mt-4">
        <Button
          title="Download as PDF"
          variant="primary"
          className="primary-btn download-btn"
          type="button"
          disabled={props.appUrl == "" || agreeTerms === false}
          onClick={downloadAsPDF}
        >
          Download as PDF
        </Button>
        <Button
          title="Download as JPG"
          variant="primary"
          className="primary-btn download-btn"
          type="button"
          disabled={props.appUrl == "" || agreeTerms === false}
          onClick={downloadAsJPG}
        >
          Download as JPG
        </Button>
        <Button
          title="Download as PNG"
          variant="primary"
          className="primary-btn download-btn"
          type="button"
          disabled={props.appUrl == "" || agreeTerms === false}
          onClick={downloadAsPNG}
        >
          Download as PNG
        </Button>
      </div>
      <div className="mt-3">
        <span className="error-msg">
          App URL need to be generated for Downloading the Sign.
        </span>
      </div>
      <div className="mt-1">
        <span className="error-msg">
        For best results use Chrome browser.         
        </span>
      </div>
      {loading && <FullLoader />}
    </div>
  );
};
export default FreeVacuumTemplate03;
